import { useEffect } from "react";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Phone, Mail, Clock, MapPin, Navigation } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";
import { LOGO_URL } from "./brand";

const PHONE = "+1 (929) 424-5955";
const PHONE_HREF = "tel:+19294245955";
const EMAIL = "info@santachiaracaffe.com";
const HOURS = "Open every day · 7:00 AM – 10:30 PM";

const LOCATIONS = [
  {
    id: "lic",
    name: "Long Island City",
    address: "5241 Center Blvd, Long Island City, NY 11101",
    tags: ["Caffetteria", "Gelateria", "Pasticceria", "Bistrot"],
    pos: [40.7438, -73.9578],
    maps:
      "https://maps.google.com/?q=5241+Center+Blvd,+Long+Island+City,+NY+11101",
  },
  {
    id: "greenpoint",
    name: "Greenpoint",
    address: "227 West St, Greenpoint, NY 11222",
    tags: ["Pizzeria", "Caffetteria", "Gelateria", "Pasticceria", "Bistrot"],
    pos: [40.7301, -73.9589],
    maps: "https://maps.google.com/?q=227+West+St,+Greenpoint,+NY+11222",
  },
];

// Santa Chiara branded pin — only the central gold symbol (the brushstroke
// between the words) inside a white circular badge with a bordeaux pointer.
const scIcon = L.divIcon({
  className: "sc-marker",
  html: `
    <div class="sc-pin">
      <div class="sc-pin-badge">
        <svg viewBox="680 18 80 232" preserveAspectRatio="xMidYMid meet" aria-hidden="true">
          <defs>
            <filter id="scPinFilter" color-interpolation-filters="sRGB">
              <feColorMatrix type="matrix" values="1 0 0 0 0  0 1 0 0 0  0 0 1 0 0  25 0 -24 0 0"/>
            </filter>
          </defs>
          <image href="${LOGO_URL}" x="0" y="0" width="1555" height="266"
            preserveAspectRatio="xMidYMid meet" filter="url(#scPinFilter)" />
        </svg>
      </div>
      <span class="sc-pin-tip"></span>
    </div>`,
  iconSize: [48, 60],
  iconAnchor: [24, 58],
  popupAnchor: [0, -54],
});

const FitBounds = () => null;

export const LocationMap = ({ open, onOpenChange }) => {
  // Ensure Leaflet recalculates size when the dialog opens.
  useEffect(() => {
    if (open) {
      const t = setTimeout(() => window.dispatchEvent(new Event("resize")), 250);
      return () => clearTimeout(t);
    }
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="location-map-dialog"
        className="p-0 overflow-hidden max-w-3xl w-[94vw] rounded-2xl border-[#D4AF37]/30"
      >
        <DialogHeader className="px-6 pt-6 pb-4">
          <DialogTitle className="font-display text-2xl text-[#800020]">
            Find Santa Chiara
          </DialogTitle>
          <DialogDescription className="text-sm text-[#4A4A4A]">
            Two locations across New York — tap a pin for details.
          </DialogDescription>
        </DialogHeader>

        <div className="h-[62vh] sm:h-[520px] w-full">
          {open && (
            <MapContainer
              center={[40.7372, -73.9584]}
              zoom={13}
              scrollWheelZoom={false}
              style={{ height: "100%", width: "100%" }}
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>'
                url="https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png"
              />
              {LOCATIONS.map((loc) => (
                <Marker key={loc.id} position={loc.pos} icon={scIcon}>
                  <Popup>
                    <div
                      data-testid={`map-popup-${loc.id}`}
                      className="min-w-[220px] font-sans"
                    >
                      <p className="font-display text-lg text-[#800020] leading-tight mb-2">
                        Santa Chiara
                        <span className="block text-xs uppercase tracking-[0.2em] text-[#D4AF37] font-semibold mt-0.5">
                          {loc.name}
                        </span>
                      </p>
                      <ul className="mb-3 space-y-0.5">
                        {loc.tags.map((t) => (
                          <li
                            key={t}
                            className="font-serif-soft text-[15px] italic text-[#800020] leading-snug"
                          >
                            {t}
                          </li>
                        ))}
                      </ul>
                      <div className="space-y-2 text-[13px] text-[#333]">
                        <p className="flex items-start gap-2">
                          <MapPin className="h-4 w-4 mt-0.5 text-[#800020] shrink-0" />
                          <span>{loc.address}</span>
                        </p>
                        <a
                          href={PHONE_HREF}
                          className="flex items-center gap-2 hover:text-[#800020]"
                        >
                          <Phone className="h-4 w-4 text-[#800020] shrink-0" />
                          <span>{PHONE}</span>
                        </a>
                        <a
                          href={`mailto:${EMAIL}`}
                          className="flex items-center gap-2 hover:text-[#800020]"
                        >
                          <Mail className="h-4 w-4 text-[#800020] shrink-0" />
                          <span>{EMAIL}</span>
                        </a>
                        <p className="flex items-start gap-2">
                          <Clock className="h-4 w-4 mt-0.5 text-[#800020] shrink-0" />
                          <span>{HOURS}</span>
                        </p>
                      </div>
                      <a
                        href={loc.maps}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`map-directions-${loc.id}`}
                        className="mt-3 inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.15em] font-semibold text-white bg-[#800020] hover:bg-[#9a1b38] px-3 py-2 rounded-full transition-colors"
                      >
                        <Navigation className="h-3.5 w-3.5" />
                        Get directions
                      </a>
                    </div>
                  </Popup>
                </Marker>
              ))}
              <FitBounds />
            </MapContainer>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
